import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
import { MapPin, Users, Clock } from 'lucide-react';
const locations = [{
  name: 'Singapore',
  country: '🇸🇬',
  region: 'Southeast Asia',
  teamSize: '12+',
  timezone: 'GMT+8',
  color: '#faff31',
  depth: 3
}, {
  name: 'Philippines',
  country: '🇵🇭',
  region: 'Southeast Asia',
  teamSize: '8+',
  timezone: 'GMT+8',
  color: '#faff31',
  depth: 2
}, {
  name: 'Brisbane',
  country: '🇦🇺',
  region: 'Australia',
  teamSize: '6+',
  timezone: 'GMT+10',
  color: '#15c8ff',
  depth: 4
}, {
  name: 'Gold Coast',
  country: '🇦🇺',
  region: 'Australia',
  teamSize: '5+',
  timezone: 'GMT+10',
  color: '#15c8ff',
  depth: 1
}, {
  name: 'Sydney',
  country: '🇦🇺',
  region: 'Australia',
  teamSize: '10+',
  timezone: 'GMT+11',
  color: '#15c8ff',
  depth: 5
}, {
  name: 'Melbourne',
  country: '🇦🇺',
  region: 'Australia',
  teamSize: '7+',
  timezone: 'GMT+11',
  color: '#15c8ff',
  depth: 3
}, {
  name: 'Perth',
  country: '🇦🇺',
  region: 'Australia',
  teamSize: '4+',
  timezone: 'GMT+8',
  color: '#15c8ff',
  depth: 2
}];
export const Contact: React.FC = () => {
  const [hoveredLocation, setHoveredLocation] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://server.fillout.com/embed/v1/';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  return <section id="contact" className="relative py-24 md:py-32 overflow-hidden bg-deep-navy/50" data-id="element-157">
      <FloatingOrbs count={10} data-id="element-158" />

      {/* Background glows */}
      <motion.div className="absolute top-0 left-0 right-0 h-[40vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.08) 0%, transparent 70%)',
      opacity: 0.6
    }} data-id="element-159" />

      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.12) 0%, transparent 60%)',
      opacity: 0.6
    }} data-id="element-160" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-161">
        {/* Section Title */}
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} data-id="element-162">
          <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-4 text-white" initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} data-id="element-163">
            Let's build your{' '}
            <span className="text-electric-yellow" data-id="element-164">intelligent system</span>
          </motion.h2>
          <motion.p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-165">
            Get in touch to discuss your Salesforce architecture and AI strategy
          </motion.p>

          {/* Purple gradient divider */}
          <motion.div className="max-w-xs mx-auto mt-8" initial={{
          scaleX: 0,
          opacity: 0
        }} whileInView={{
          scaleX: 1,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} viewport={{
          once: true
        }} data-id="element-166">
            <div className="h-[1px]" style={{
            background: 'linear-gradient(90deg, rgba(79, 12, 154, 0), rgba(79, 12, 154, 0.6), rgba(79, 12, 154, 0))'
          }} data-id="element-167" />
          </motion.div>

          {/* Fillout Popup Embed */}
          <motion.div className="mt-6 flex justify-center" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} viewport={{
          once: true
        }} data-id="element-168">
            <div data-fillout-id="oxJy4k63Bfus" data-fillout-embed-type="popup" data-fillout-button-text="Contact Us" data-fillout-dynamic-resize data-fillout-inherit-parameters data-fillout-popup-size="medium" data-id="element-169" />
          </motion.div>
        </motion.div>

        {/* Paper-Cut Style Presence Section */}
        <motion.div className="max-w-6xl mx-auto" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} data-id="element-170">
          {/* Section Title */}
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-171">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-2" data-id="element-172">
              Our Presence
            </h3>
            <p className="text-white/70 text-sm md:text-base" data-id="element-173">
              Supporting organisations across Asia-Pacific
            </p>
          </motion.div>

          {/* Desktop: Layered Paper-Cut Cards */}
          <div className="hidden lg:block relative" data-id="element-174">
            {/* Textured background layer */}
            <div className="absolute inset-0 rounded-2xl opacity-30" style={{
            background: `
                  repeating-linear-gradient(
                    45deg,
                    rgba(79, 12, 154, 0.03),
                    rgba(79, 12, 154, 0.03) 10px,
                    rgba(79, 12, 154, 0.05) 10px,
                    rgba(79, 12, 154, 0.05) 20px
                  )
                `
          }} data-id="element-175" />

            {/* Layered location cards */}
            <div className="relative grid grid-cols-2 gap-6 p-8" data-id="element-176">
              {locations.map((location, index) => <motion.div key={location.name} className="relative cursor-pointer group" initial={{
              opacity: 0,
              y: 30,
              rotateX: -15
            }} whileInView={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} onMouseEnter={() => setHoveredLocation(location.name)} onMouseLeave={() => setHoveredLocation(null)} whileHover={{
              y: -8,
              transition: {
                duration: 0.3
              }
            }} style={{
              transformStyle: 'preserve-3d',
              perspective: '1000px'
            }} data-id="element-177">
                  {/* Paper cut-out window with glowing light beneath */}
                  <div className="relative rounded-xl overflow-hidden" style={{
                transform: `translateZ(${location.depth * 8}px)`,
                boxShadow: `
                        0 ${location.depth * 4}px ${location.depth * 8}px rgba(0, 0, 0, 0.3),
                        0 ${location.depth * 2}px ${location.depth * 4}px rgba(0, 0, 0, 0.2),
                        inset 0 -2px 8px rgba(0, 0, 0, 0.1)
                      `
              }} data-id="element-178">
                    {/* Glowing light layer beneath */}
                    <motion.div className="absolute inset-0" style={{
                  background: `radial-gradient(ellipse 120% 120% at 50% 50%, ${location.color}40 0%, ${location.color}20 40%, transparent 70%)`,
                  filter: 'blur(20px)'
                }} animate={{
                  opacity: hoveredLocation === location.name ? 1 : 0.6,
                  scale: hoveredLocation === location.name ? 1.1 : 1
                }} transition={{
                  duration: 0.4
                }} data-id="element-179" />

                    {/* Paper layer with texture */}
                    <div className="relative backdrop-blur-sm border-2 p-6" style={{
                  backgroundColor: 'rgba(28, 20, 61, 0.85)',
                  borderColor: `${location.color}30`,
                  backgroundImage: `
                          radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
                          radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
                        `
                }} data-id="element-180">
                      {/* Torn paper edge effect */}
                      <div className="absolute top-0 left-0 right-0 h-1" style={{
                    background: `linear-gradient(90deg, ${location.color}20, ${location.color}40, ${location.color}20)`,
                    clipPath: 'polygon(0 0, 5% 100%, 10% 0, 15% 100%, 20% 0, 25% 100%, 30% 0, 35% 100%, 40% 0, 45% 100%, 50% 0, 55% 100%, 60% 0, 65% 100%, 70% 0, 75% 100%, 80% 0, 85% 100%, 90% 0, 95% 100%, 100% 0)'
                  }} data-id="element-181" />

                      {/* Location content */}
                      <div className="flex items-start justify-between mb-4" data-id="element-182">
                        <div data-id="element-183">
                          <div className="flex items-center gap-2 mb-1" data-id="element-184">
                            <span className="text-2xl" data-id="element-185">{location.country}</span>
                            <h4 className="text-xl font-heading font-bold text-electric-yellow" data-id="element-186">
                              {location.name}
                            </h4>
                          </div>
                          <p className="text-xs text-white/60" data-id="element-187">
                            {location.region}
                          </p>
                        </div>
                        <motion.div className="w-3 h-3 rounded-full" style={{
                      backgroundColor: location.color,
                      boxShadow: `0 0 12px ${location.color}`
                    }} animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-188" />
                      </div>

                      {/* Gradient shadow at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none" style={{
                    background: `linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent)`
                  }} data-id="element-189" />
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Mobile: Accordion-style expanding layers */}
          <div className="lg:hidden space-y-3" data-id="element-190">
            {locations.map((location, index) => <motion.div key={location.name} className="relative" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} data-id="element-191">
                {/* Paper layer */}
                <motion.div className="relative rounded-lg overflow-hidden cursor-pointer" onClick={() => setExpandedMobile(expandedMobile === location.name ? null : location.name)} style={{
              boxShadow: `
                      0 4px 12px rgba(0, 0, 0, 0.3),
                      0 2px 6px rgba(0, 0, 0, 0.2),
                      inset 0 -1px 4px rgba(0, 0, 0, 0.1)
                    `
            }} whileTap={{
              scale: 0.98
            }} data-id="element-192">
                  {/* Glowing light beneath */}
                  <motion.div className="absolute inset-0" style={{
                background: `radial-gradient(ellipse 120% 120% at 50% 50%, ${location.color}30 0%, ${location.color}15 40%, transparent 70%)`,
                filter: 'blur(15px)'
              }} animate={{
                opacity: expandedMobile === location.name ? 1 : 0.5
              }} data-id="element-193" />

                  {/* Paper content */}
                  <div className="relative backdrop-blur-sm border-2 p-4" style={{
                backgroundColor: 'rgba(28, 20, 61, 0.85)',
                borderColor: `${location.color}30`,
                backgroundImage: `
                        radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)
                      `
              }} data-id="element-194">
                    {/* Torn edge */}
                    <div className="absolute top-0 left-0 right-0 h-1" style={{
                  background: `linear-gradient(90deg, ${location.color}20, ${location.color}40, ${location.color}20)`,
                  clipPath: 'polygon(0 0, 10% 100%, 20% 0, 30% 100%, 40% 0, 50% 100%, 60% 0, 70% 100%, 80% 0, 90% 100%, 100% 0)'
                }} data-id="element-195" />

                    {/* Header */}
                    <div className="flex items-center justify-between" data-id="element-196">
                      <div className="flex items-center gap-2" data-id="element-197">
                        <span className="text-xl" data-id="element-198">{location.country}</span>
                        <h4 className="text-lg font-heading font-bold text-electric-yellow" data-id="element-199">
                          {location.name}
                        </h4>
                      </div>
                      <motion.div animate={{
                    rotate: expandedMobile === location.name ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-200">
                        <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-201">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-202" />
                        </svg>
                      </motion.div>
                    </div>

                    {/* Expanded content */}
                    <AnimatePresence data-id="element-203">
                      {expandedMobile === location.name && <motion.div className="mt-4 pt-4 border-t" style={{
                    borderColor: `${location.color}20`
                  }} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} data-id="element-204">
                          <p className="text-sm text-white/60" data-id="element-205">
                            {location.region}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
};